import {
  ISelection,
  IAnalys,
} from '../../../../../../Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import {
  IFavorites,
  IAnalysFavoriteInfo,
  EKAnalysGroupingModeType,
  FAVORITE_NO_UTDELNING_LOPNR_KEY,
} from '../EkonomiskAnalysFavoriteAnalysDialog';
import { IChangedAnalysInfo } from '../Queries/ChangeAnalysFavoriteMutation/ChangeAnalysFavoriteMutation';
import { ISection } from '../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { IStartAnalysInfo } from '../Queries/ChangeStartAnalysMutation/ChangeStartAnalysMutation';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../../../../../Utils/mergeSort';
import { IKombKodbehVisibility } from '../Components/EkonomiskAnalysFavoriteOptionsMenu';
import { equals } from 'ramda';
import { IAnalysData } from '../Components/EkonomiskAnalysFavoriteAnalysList';
import { ITreeSection } from '../../../../../../../../../../../BaseComponents/TreeSelect/TreeSelectUtils';

/** Extracts all data to store favorite information from a list of ISelections */
export const getInitialData = (
  analyser: ISelection[]
): { favorites: IFavorites; categories: string[] } => {
  const fav: IFavorites = {};
  const cat: string[] = [];

  analyser.forEach(kodbeh => {
    fav[kodbeh.id] = {};
    kodbeh.analysList.forEach(analys => {
      if (analys.kategori !== '' && !cat.includes(analys.kategori)) {
        cat.push(analys.kategori);
      }

      const favInfo: IAnalysFavoriteInfo = {
        analysId: analys.id,
        kodBehId: kodbeh.id,
        favoritLopnr: analys.favoritLopnr,
        utdelatLopnr: analys.utdelatLopnr,
        isFavorit: analys.favoritLopnr !== null,
        category: analys.kategori,
        grundurvalId: analys.baseSelection.id,
      };

      const utdelningLopnrKey = getUtdelningLopnrFavIdKey(analys.utdelatLopnr);

      if (fav[kodbeh.id][analys.id] === undefined) {
        fav[kodbeh.id][analys.id] = {};
      }
      fav[kodbeh.id][analys.id][utdelningLopnrKey] = favInfo;
    });
  });

  return { favorites: fav, categories: cat };
};

export const getUtdelningLopnrFavIdKey = (
  utdelningLopnr: number | null | undefined
): string =>
  utdelningLopnr != null
    ? utdelningLopnr.toString()
    : FAVORITE_NO_UTDELNING_LOPNR_KEY;

export const isFavorite = (
  kodbehId: string,
  analysId: string,
  utdelningLopnr: number | null,
  favorites: IFavorites
) => {
  const utdelningLopnrKey = getUtdelningLopnrFavIdKey(utdelningLopnr);

  if (
    !favorites[kodbehId] ||
    !favorites[kodbehId][analysId] ||
    !favorites[kodbehId][analysId][utdelningLopnrKey]
  ) {
    return false;
  }

  return favorites[kodbehId][analysId][utdelningLopnrKey].isFavorit;
};

/** Returns a list of all favorites that has been changed since opening the modal */
export const getAllChanged = (
  favorites: IFavorites,
  originalFavorites: IFavorites
): IChangedAnalysInfo[] => {
  const res: IChangedAnalysInfo[] = [];

  iterateFavorites(
    favorites,
    analys => {
      const utdelningLopnrKey = getUtdelningLopnrFavIdKey(analys.utdelatLopnr);

      return (
        analys.isFavorit !==
        originalFavorites[analys.kodBehId][analys.analysId][utdelningLopnrKey]
          .isFavorit
      );
    },
    analys => res.push(analys)
  );

  return res;
};

/** Executes action on a specific item in favorites, if select returns true on that item */
export const iterateFavorites = (
  favorites: IFavorites,
  select: (favorite: IAnalysFavoriteInfo) => boolean,
  action: (favorite: IAnalysFavoriteInfo) => void
): void => {
  Object.keys(favorites).forEach(kodBehId => {
    Object.keys(favorites[kodBehId]).forEach(analysId => {
      Object.keys(favorites[kodBehId][analysId]).forEach(utdelningLopnr => {
        const analys = favorites[kodBehId][analysId][utdelningLopnr];

        if (select(analys)) {
          action(analys);
        }
      });
    });
  });
};

/** Change all items that has matching category.
 * If any of them are not a favorite, they all become favorites, otherwise all are set to not be favorites
 */
export const changeAllInCategory = (
  category: string,
  favorites: IFavorites
): IFavorites => {
  // Check if we should deselect all, or select all
  let allIsChecked: boolean = true;
  iterateFavorites(
    favorites,
    analys => analys && analys.category === category,
    analys => {
      if (!analys.isFavorit) {
        allIsChecked = false;
      }
    }
  );

  // Do the thing
  iterateFavorites(
    favorites,
    analys => analys && analys.category === category,
    analys => (analys.isFavorit = !allIsChecked)
  );

  return favorites;
};

/** Change all items that has matching kodBehId.
 * If any of them are not a favorite, they all become favorites, otherwise all are set to not be favorites
 */
export const changeAllInKodBeh = (kodBehId: string, favorites: IFavorites) => {
  let allIsChecked: boolean = true;

  iterateFavorites(
    favorites,
    analys => analys.kodBehId === kodBehId,
    analys => {
      if (!analys.isFavorit) {
        allIsChecked = false;
      }
    }
  );

  iterateFavorites(
    favorites,
    analys => analys.kodBehId === kodBehId,
    analys => (analys.isFavorit = !allIsChecked)
  );

  return favorites;
};

/**
 * Converts ISelection[] to ISection[], to match with EkonomiskAnalysSelectList component
 * Each section is a kodbehörighet, with analyser as data.
 */
export const analyserToKodBehSections = (
  analyser: ISelection[]
): ISection[] => {
  return analyser.map(kodBeh => ({
    name: kodBeh.name,
    value: kodBeh.id,
    kombKodbeh: kodBeh.kombinerad,
    items: kodBeh.analysList.map(analys => {
      const data: IAnalysSuggestion = {
        ...analys,
        kodBehName: kodBeh.name,
        kodBehId: kodBeh.id,
        kodBehKombinerad: kodBeh.kombinerad,
      };

      return {
        name: analys.name,
        value: analys.id,
        data,
      };
    }),
  }));
};

/**
 * Converts ISelection[] to ISection[], to match with EkonomiskAnalysSelectList component
 * Each section is an analys category, with analyser as data.
 */
export const analyserToCategorySections = (
  analyser: ISelection[]
): ISection[] => {
  let categories: ISection[] = [];

  analyser.forEach(kodBeh => {
    kodBeh.analysList.forEach(analys => {
      if (!categories.find(c => c.name === analys.kategori)) {
        categories.push({
          name: analys.kategori,
          value: analys.kategori,
          items: [],
        });
      }
    });
  });

  categories.forEach(cat => {
    analyser.forEach(kodBeh => {
      kodBeh.analysList.forEach(analys => {
        if (analys.kategori === cat.name) {
          const data: IAnalysSuggestion = {
            ...analys,
            kodBehName: kodBeh.name,
            kodBehId: kodBeh.id,
            kodBehKombinerad: kodBeh.kombinerad,
            kategori: analys.kategori,
          };

          cat.items.push({
            name: analys.name,
            value: analys.id,
            data,
          });
        }
      });
    });

    cat.items = mergeSort(cat.items, (a, b) => {
      const result = a.name.localeCompare(b.name, 'sv', {
        numeric: true,
        sensitivity: 'base',
      });

      if (result === 0) {
        if (a.data.kodBehKombinerad && !b.data.kodBehKombinerad) {
          return -1;
        } else if (b.data.kodBehKombinerad && !a.data.kodBehKombinerad) {
          return 1;
        } else {
          return a.data.kodBehName.localeCompare(b.data.kodBehName, 'sv', {
            numeric: true,
            sensitivity: 'base',
          });
        }
      }

      return result;
    });
  });

  categories = mergeSort(categories, (a, b) =>
    a.name.localeCompare(b.name, 'sv', {
      numeric: true,
      sensitivity: 'base',
    })
  );
  return categories;
};

/**
 * Converts ISelection[] to ISection[], to match with EkonomiskAnalysSelectList component
 */
export const analyserToSections = (
  analyser: ISelection[],
  sortMode: EKAnalysGroupingModeType
): ISection[] => {
  if (sortMode === 'kodbeh') {
    return analyserToKodBehSections(analyser);
  } else {
    return analyserToCategorySections(analyser);
  }
};

/**
 * Converts ISelection[] to ITreeSection[], to match with TreeSelct component
 * Each section is a kodbehörighet, with analyser as data.
 */
export const analyserToKodBehTreeSections = (
  analyser: ISelection[],
  favorites: IFavorites
): ITreeSection<IAnalysData>[] => {
  return analyser.map(kodBeh => ({
    name: kodBeh.name,
    value: kodBeh.id,
    id: kodBeh.id,
    open: true,
    data: {
      kodBehName: kodBeh.name,
      kodBehId: kodBeh.id,
      kodBehKombinerad: kodBeh.kombinerad,
    },
    subList: kodBeh.analysList.map(analys => ({
      name: analys.name,
      value: analys.id,
      id: analys.id,
      disabled: analys.obligatorisk
        ? {
            value: true,
            tooltip:
              'Obligatorisk och går därmed inte att ta bort från analysmenyn',
            labelSuffix: '*',
          }
        : undefined,
      checked: isFavorite(kodBeh.id, analys.id, analys.utdelatLopnr, favorites),
      data: {
        baseSelection: analys.baseSelection,
        kodBehName: kodBeh.name,
        kodBehId: kodBeh.id,
        kodBehKombinerad: kodBeh.kombinerad,
        utdelatLopnr: analys.utdelatLopnr,
        idKod: analys.idkod,
        kategori: analys.kategori,
      },
    })),
  }));
};

/**
 * Converts ISelection[] to ITreeSection[], to match with TreeSelect component
 * Each section is an analys category, with analyser as data.
 */
export const analyserToCategoryTreeSections = (
  analyser: ISelection[],
  favorites: IFavorites
): ITreeSection<IAnalysData>[] => {
  let categories: ITreeSection<IAnalysData>[] = [];

  analyser.forEach(kodBeh => {
    kodBeh.analysList.forEach(analys => {
      if (!categories.find(c => c.name === analys.kategori)) {
        categories.push({
          name: analys.kategori,
          value: analys.kategori,
          id: analys.kategori,
          disabled: analys.obligatorisk
            ? {
                value: true,
                tooltip:
                  'Obligatorisk och går därmed inte att ta bort från analysmenyn',
                labelSuffix: '*',
              }
            : undefined,
          data: {
            baseSelection: analys.baseSelection,
            kodBehId: kodBeh.id,
            kodBehName: kodBeh.name,
            kodBehKombinerad: kodBeh.kombinerad,
            idKod: analys.idkod,
            utdelatLopnr: analys.utdelatLopnr,
            kategori: analys.kategori,
          },
          open: true,
          subList: [],
        });
      }
    });
  });

  categories.forEach(cat => {
    analyser.forEach(kodBeh => {
      kodBeh.analysList.forEach(analys => {
        if (analys.kategori === cat.name) {
          const data: IAnalysSuggestion = {
            ...analys,
            kodBehName: kodBeh.name,
            kodBehId: kodBeh.id,
            kodBehKombinerad: kodBeh.kombinerad,
          };

          cat.subList?.push({
            name: analys.name,
            value: analys.id,
            id: analys.id,
            checked: isFavorite(
              kodBeh.id,
              analys.id,
              analys.utdelatLopnr,
              favorites
            ),
            data: {
              kodBehId: data.kodBehId,
              kodBehKombinerad: data.kodBehKombinerad,
              kodBehName: data.kodBehName,
              baseSelection: data.baseSelection,
              idKod: data.idkod,
              kategori: data.kategori,
              utdelatLopnr: data.utdelatLopnr,
            },
          });
        }
      });
    });

    cat.subList = mergeSort(cat.subList ?? [], (a, b) => {
      const result = a.name.localeCompare(b.name, 'sv', {
        numeric: true,
        sensitivity: 'base',
      });

      if (result === 0) {
        if (a.data?.kodBehKombinerad && !b.data?.kodBehKombinerad) {
          return -1;
        } else if (b.data?.kodBehKombinerad && !a.data?.kodBehKombinerad) {
          return 1;
        } else {
          return stringCompareNumeric(
            a.data?.kodBehName ?? '',
            b.data?.kodBehName ?? ''
          );
        }
      }

      return result;
    });
  });

  categories = mergeSort(categories, (a, b) =>
    a.name.localeCompare(b.name, 'sv', {
      numeric: true,
      sensitivity: 'base',
    })
  );

  return categories;
};

/**
 * Converts ISelection[] to ITreeSection[], to match with TreeSelect component
 */
export const analyserToTreeSections = (
  analyser: ISelection[],
  sortMode: EKAnalysGroupingModeType,
  favorites: IFavorites
): ITreeSection<IAnalysData>[] => {
  if (sortMode === 'kodbeh') {
    return analyserToKodBehTreeSections(analyser, favorites);
  } else {
    return analyserToCategoryTreeSections(analyser, favorites);
  }
};

export const isAnyButNotAllChecked = (
  favorites: IFavorites,
  section: ISection
): boolean => {
  return (
    !section.items.every(
      analys =>
        favorites[analys.data.kodBehId][analys.data.id][
          getUtdelningLopnrFavIdKey(analys.data.utdelatLopnr)
        ].isFavorit
    ) &&
    section.items.some(
      analys =>
        favorites[analys.data.kodBehId][analys.data.id][
          getUtdelningLopnrFavIdKey(analys.data.utdelatLopnr)
        ].isFavorit
    )
  );
};

type IsSameAnalysInfo = Pick<
  IStartAnalysInfo,
  'utdelningLopnr' | 'kodbehorighet' | 'rapportId' | 'idKod'
>;

/**
 * Kollar om två analyser har samma id, kodbehörighet och grundurval
 */
export const isSameAnalys = (
  analysA: IsSameAnalysInfo,
  analysB: IsSameAnalysInfo
): boolean =>
  analysA.kodbehorighet === analysB.kodbehorighet &&
  (analysA.idKod
    ? analysA.idKod === analysB.idKod
    : analysA.rapportId === analysB.rapportId) &&
  analysA.utdelningLopnr === analysB.utdelningLopnr;

export const startAnalysChanged = (
  oldAnalys?: IStartAnalysInfo,
  newAnalys?: IStartAnalysInfo
): boolean => {
  if ((!oldAnalys && newAnalys) || (oldAnalys && !newAnalys)) {
    return true;
  } else if (oldAnalys && newAnalys) {
    return !isSameAnalys(oldAnalys, newAnalys);
  }

  return false;
};

export interface IAnalysSuggestion extends IAnalys {
  kodBehName: string;
  kodBehId: string;
  kodBehKombinerad: boolean;
}

export const isStartAnalys = (
  analys: ITreeSection<IAnalysData>,
  startAnalys: IStartAnalysInfo
): boolean =>
  isSameAnalys(startAnalys, {
    utdelningLopnr: analys.data?.utdelatLopnr ?? null,
    rapportId: analys.value,
    idKod: analys.data?.idKod,
    kodbehorighet: analys.data?.kodBehId ?? '',
  });

export const analysGroupingModeChanged = (
  initialGroupMode: EKAnalysGroupingModeType,
  currentGroupMode: EKAnalysGroupingModeType
) => initialGroupMode !== currentGroupMode;

export const kombKodbehVisibilityChanged = (
  initialVisibility: IKombKodbehVisibility,
  currentVisibility: IKombKodbehVisibility
) => {
  return !equals(initialVisibility, currentVisibility);
};

export const updateStartanalysAtLoginChanged = (
  initial: boolean,
  current: boolean
) => {
  return initial !== current;
};
